import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  Chip,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import {
  postAlertModalAction,
  modifyAlertModalAction,
} from '../../store/actions/alerts';
import octopusInstance from '../../services/octopus/octopusInstance';
import { getExchangeCode } from '../../services/exchanges';

const CONDITION_OPTIONS = [
  { value: 'greater_than', label: 'Greater than', range: false, prettyOp: '>' },
  { value: 'lesser_than', label: 'Lesser than', range: false, prettyOp: '<' },
  { value: 'crosses_up', label: 'Crosses up', range: false, prettyOp: '↗' },
  { value: 'crosses_down', label: 'Crosses down', range: false, prettyOp: '↘' },
  { value: 'in_between', label: 'Inside range', range: true, prettyOp: 'in' },
  { value: 'out_of_range', label: 'Outside range', range: true, prettyOp: 'out of' },
  { value: 'enter_range', label: 'Entering range', range: true, prettyOp: 'enters' },
  { value: 'exit_range', label: 'Exiting range', range: true, prettyOp: 'exits' },
];

const isRangeCondition = (cond) => CONDITION_OPTIONS.find((o) => o.value === cond)?.range === true;

// Backend-friendly user_message — matches the captur_web_app format:
// "{trading_symbol} {condition_snake_case} {value}" (or "{lo}-{hi}" for range).
// No currency symbols / non-ASCII operators — those can be rejected by the BE.
const buildUserMessage = ({ trading_symbol, condition, lower, upper }) => {
  const opt = CONDITION_OPTIONS.find((o) => o.value === condition);
  const isRange = !!opt?.range;
  const value = isRange ? `${lower}-${upper}` : `${lower}`;
  return `${trading_symbol} ${condition} ${value}`;
};

const defaultExpiry = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d;
};

// Convert a Date to the "YYYY-MM-DDTHH:MM" string the native datetime-local input wants.
const dateToLocalInputValue = (d) => {
  if (!d || Number.isNaN(d.getTime?.())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const PriceAlertModal = ({
  open,
  onClose,
  instrument,
  ltp,
  isModify = false,
  existingAlert = null,
}) => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [condition, setCondition] = useState('greater_than');
  const [lower, setLower] = useState('');
  const [upper, setUpper] = useState('');
  const [expiry, setExpiry] = useState(defaultExpiry());
  const [frequency, setFrequency] = useState('once');
  const [waitMinutes, setWaitMinutes] = useState(60);
  const [submitting, setSubmitting] = useState(false);
  const wasOpenRef = useRef(false);
  const [liveLtp, setLiveLtp] = useState(null);

  // Live WebSocket LTP subscription
  useEffect(() => {
    if (!open || !instrument?.token || !instrument?.exchange) {
      setLiveLtp(null);
      return;
    }

    setLiveLtp(Number(ltp) || Number(instrument.ltp) || null);

    const subscriptionLocation = `alert-modal-${instrument.token}`;
    const handler = octopusInstance.wsHandler({
      messageType: 'CompactMarketDataMessage',
      subscriptionLocation,
      payload: {
        exchangeCode: getExchangeCode(instrument.exchange),
        instrumentToken: Number(instrument.token),
      },
    });

    handler.subscribe(({ msg }) => {
      const nextLtp = msg.ltp || msg.lastTradedPrice || msg.last_trade_price || msg.Price || 0;
      if (nextLtp) {
        setLiveLtp(nextLtp);
      }
    });

    return () => {
      handler.unsubscribe();
    };
  }, [open, instrument?.token, instrument?.exchange, ltp, instrument?.ltp]);

  // Seed lower bound only once when liveLtp becomes available if lower is currently empty
  useEffect(() => {
    if (open && !isModify && liveLtp && !lower) {
      setLower(String(liveLtp));
    }
  }, [open, isModify, liveLtp, lower]);

  // Seed state ONLY when the modal transitions to open. Prevents the seed
  // from re-firing on every parent re-render (e.g. socket LTP ticks change
  // the `instrument` reference) which would constantly reset user input.
  useEffect(() => {
    if (!open) { wasOpenRef.current = false; return; }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;

    if (isModify && existingAlert) {
      const cond = existingAlert.condition_type || existingAlert.condition || 'greater_than';
      const values = Array.isArray(existingAlert.user_set_values) ? existingAlert.user_set_values : [];
      setCondition(cond);
      setLower(values[0] !== undefined ? String(values[0]) : '');
      setUpper(values[1] !== undefined ? String(values[1]) : '');
      setFrequency(existingAlert.frequency || 'once');
      const wt = Number(existingAlert.wait_time) || 3600;
      setWaitMinutes(Math.max(1, Math.round(wt / 60)));
      setExpiry(existingAlert.expiry ? new Date(Number(existingAlert.expiry) * 1000) : defaultExpiry());
    } else {
      // sensible defaults from instrument LTP
      const seedPrice = Number(ltp) || Number(instrument?.ltp) || '';
      setCondition('greater_than');
      setLower(seedPrice ? String(seedPrice) : '');
      setUpper('');
      setFrequency('once');
      setWaitMinutes(60);
      setExpiry(defaultExpiry());
    }
  }, [open, isModify, existingAlert, ltp, instrument]);

  const range = isRangeCondition(condition);
  const rangeError = useMemo(() => {
    if (!range) return null;
    const lo = Number(lower);
    const hi = Number(upper);
    if (!lower || !upper) return null;
    if (Number.isNaN(lo) || Number.isNaN(hi)) return 'Enter valid numbers';
    if (hi <= lo) return 'Upper bound must be greater than lower bound';
    return null;
  }, [range, lower, upper]);

  const expiryError = useMemo(() => {
    if (!expiry) return 'Choose an expiry date and time';
    if (new Date(expiry).getTime() <= Date.now()) return 'Expiry must be in the future';
    return null;
  }, [expiry]);

  const canSubmit =
    !!instrument?.token &&
    !!instrument?.exchange &&
    !!lower &&
    !Number.isNaN(Number(lower)) &&
    (!range || (!!upper && !rangeError)) &&
    !expiryError &&
    (frequency === 'once' || Number(waitMinutes) >= 1);

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);

    const user_set_values = range
      ? [Number(lower), Number(upper)]
      : [Number(lower)];

    const payload = {
      exchange: instrument.exchange,
      instrument_token: String(instrument.token),
      condition,
      user_set_values,
      frequency,
      wait_time: String(Math.max(60, Math.round((Number(waitMinutes) || 60) * 60))),
      expiry: Math.floor(new Date(expiry).getTime() / 1000),
      state_after_expiry: 'deleted',
      user_message: buildUserMessage({
        trading_symbol: instrument.trading_symbol || instrument.display_name || '',
        condition,
        lower,
        upper,
      }),
    };

    const result = isModify && existingAlert?.id
      ? await dispatch(modifyAlertModalAction(existingAlert.id, payload))
      : await dispatch(postAlertModalAction(payload));

    setSubmitting(false);
    if (result?.status === 'success') {
      enqueueSnackbar(isModify ? 'Alert updated' : 'Alert created', { variant: 'success' });
      onClose?.();
    } else {
      enqueueSnackbar(result?.message || 'Failed to save alert', { variant: 'error' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            {isModify ? 'Modify Price Alert' : 'Create Price Alert'}
          </Typography>
          {instrument && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {instrument.display_name || instrument.trading_symbol}
              </Typography>
              {instrument.exchange && <Chip size="small" label={instrument.exchange} />}
              {(liveLtp || ltp || instrument.ltp) && (
                <Typography variant="caption" color="text.secondary">
                  LTP ₹{Number(liveLtp || ltp || instrument.ltp).toFixed(instrument.price_precision || 2)}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            select
            label="Trigger condition"
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            fullWidth
            size="small"
          >
            {CONDITION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'grid', gridTemplateColumns: range ? '1fr 1fr' : '1fr', gap: 2 }}>
            <TextField
              label={range ? 'Lower bound' : 'Alert price'}
              value={lower}
              onChange={(e) => setLower(e.target.value)}
              type="number"
              size="small"
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              inputProps={{ step: 'any', min: 0 }}
            />
            {range && (
              <TextField
                label="Upper bound"
                value={upper}
                onChange={(e) => setUpper(e.target.value)}
                type="number"
                size="small"
                fullWidth
                error={!!rangeError}
                helperText={rangeError || ' '}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                inputProps={{ step: 'any', min: 0 }}
              />
            )}
          </Box>

          <TextField
            label="Expiry"
            type="datetime-local"
            value={dateToLocalInputValue(expiry)}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) { setExpiry(null); return; }
              const next = new Date(v);
              if (!Number.isNaN(next.getTime())) setExpiry(next);
            }}
            size="small"
            fullWidth
            error={!!expiryError}
            helperText={expiryError || 'Alert is deleted after this time'}
            InputLabelProps={{ shrink: true }}
          />

          <Box>
            <Typography variant="caption" color="text.secondary">Frequency</Typography>
            <RadioGroup
              row
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
            >
              <FormControlLabel value="once" control={<Radio size="small" />} label="Once" />
              <FormControlLabel value="recurring" control={<Radio size="small" />} label="Recurring" />
            </RadioGroup>
          </Box>

          <TextField
            label="Notification interval (minutes)"
            value={waitMinutes}
            onChange={(e) => setWaitMinutes(e.target.value)}
            type="number"
            size="small"
            fullWidth
            disabled={frequency === 'once'}
            helperText={
              frequency === 'once'
                ? 'Not used when frequency is Once'
                : 'How long to wait before firing again'
            }
            inputProps={{ min: 1, step: 1 }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isModify ? 'Save changes' : 'Create alert'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PriceAlertModal;
